'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { createClient } from '@/utils/supabase/client';
import { getUserProfile, updateUserProfile } from '@/utils/supabase/queries';

export default function OnboardingPopup() {
  const supabase = createClient();

  const [otherUsage, setOtherUsage] = useState('');
  const [profile, setProfile] = useState<{
    completed_onboarding: boolean;
  } | null>(null);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [user, setUser] = useState<{
    id: string;
    email: string | undefined;
  } | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUser({ id: user.id, email: user.email });
        setIsUserLoaded(true);
      }
    };
    fetchUser();
  }, [supabase]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.id) {
        const userProfile = await getUserProfile(supabase, user.id);
        setProfile(userProfile);
        setIsProfileLoaded(true);
      }
    };
    fetchProfile();
  }, [user, supabase]);

  const handleSubmit = async () => {
    if (user?.id) {
      setIsUpdating(true);
      await updateUserProfile(supabase, {
        id: user.id,
        usage: otherUsage,
        completed_onboarding: true,
      });
      setProfile({ ...profile, completed_onboarding: true });
      setIsUpdating(false);
    }
  };

  const handleSkip = async () => {
    if (user?.id) {
      setIsUpdating(true);
      await updateUserProfile(supabase, {
        id: user.id,
        completed_onboarding: true,
      });
      setProfile({ ...profile, completed_onboarding: true });
      setIsUpdating(false);
    }
  };

  if (
    !isUserLoaded ||
    !user ||
    !isProfileLoaded ||
    (isProfileLoaded && profile?.completed_onboarding)
  ) {
    return null;
  }

  return (
    <Dialog open={!profile?.completed_onboarding}>
      <DialogContent
        showClose={false}
        className={'max-w-[400px] border-none lg:max-w-[600px]'}
      >
        <DialogTitle className="sr-only">
          Help Us Tailor Your Beatwave Experience!
        </DialogTitle>
        <DialogHeader
          className={'text-2xl font-bold uppercase italic text-wave-brown'}
        >
          Help Us Tailor Your Beatwave Experience!
        </DialogHeader>
        <DialogDescription className={'flex flex-col space-y-4'}>
          <p>
            We are constantly improving Beatwave, and tailoring your experience.
            To help us with this, could you tell us in a few words what you are
            using Beatwave for?
          </p>

          <Textarea
            value={otherUsage}
            onChange={(e) => setOtherUsage(e.target.value)}
            rows={4}
          />
        </DialogDescription>
        <DialogFooter>
          <Button
            variant={'ghost'}
            size={'lg'}
            onClick={handleSkip}
            disabled={isUpdating}
          >
            Skip
          </Button>
          <Button
            variant={'default'}
            size={'lg'}
            onClick={handleSubmit}
            disabled={otherUsage.length < 1 || isUpdating}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}